import { Injectable } from '@angular/core';
import { grpc } from '@improbable-eng/grpc-web';
import { ProtobufMessage } from '@improbable-eng/grpc-web/dist/typings/message';
import { UnaryOutput } from '@improbable-eng/grpc-web/dist/typings/unary';
import { BrowserHeaders } from 'browser-headers';
import { Observable, share, BehaviorSubject, finalize, from, switchMap } from 'rxjs';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { MessageLevel, PgMessagesService } from 'src/app/shared/services/pg-messages.service';
import { grpcApiUrl } from 'src/environments/environment.service';

interface UnaryMethodDefinitionishR extends grpc.UnaryMethodDefinition<any, any> {
  requestStream: any;
  responseStream: any;
}

type UnaryMethodDefinitionish = UnaryMethodDefinitionishR;

interface GrpcCallOptions {
  transport?: grpc.TransportFactory;
  streamingTransport?: grpc.TransportFactory;
  debug?: boolean;
  metadata?: grpc.Metadata;
  upStreamRetryCodes?: number[];
}

@Injectable({ providedIn: 'root' })
export class GrpcWebErrrHandlingImpl {
  private host: string;

  private concurrentRequests = 0;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private _keycloakService: KeycloakService,
    private _pgMessagesService: PgMessagesService,
  ) {
    this.host = grpcApiUrl;
  }

  handleError(response: UnaryOutput<ProtobufMessage>) {
    console.error(response);
    switch (response.status) {
    case 1: // CANCELLED
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Info,
        message: 'Your operation is cancelled (1).',
        topic: 'Services'
      });
      break;
    case 2: // UNKNOWN
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Critical,
        message: 'Your operation cannot be performed. Please get in touch with admin (2).',
        topic: 'Services'
      });
      break;
    case 3: // INVALID_ARGUMENT
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        message: 'There is a problem with your request. Please get in touch with admin (3).',
        topic: 'Services'
      });
      break;
    case 4: // DEADLINE_EXCEEDED (time out)
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        message: 'Your operation timed out. Please try again (4).',
        topic: 'Services'
      });
      break;
    case 5: // NOT_FOUND
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Info,
        message: 'Your requested record is not found (5).',
        topic: 'Services'
      });
      break;
    case 6: // ALREADY_EXISTS
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Info,
        message: 'The record you are trying to add already exists on the system (6).',
        topic: 'Services'
      });
      break;
    case 7: // PERMISSION_DENIED
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Critical,
        message: 'You do not have persmission to access this resource. Please get in touch with admin (7).',
        topic: 'Services'
      });
      break;
    case 8: // RESOURCE_EXHAUSTED
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Critical,
        message: 'Server is too busy to handle your request (8).',
        topic: 'Services'
      });
      break;
    case 9: // FAILED_PRECONDITION
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        message: 'There is an unknown error. Please get in touch with admin (9).',
        topic: 'Services'
      });
      break;
    case 10: // ABORTED
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Info,
        message: 'Your operation is aborted (10).',
        topic: 'Services'
      });
      break;
    case 11: // OUT_OF_RANGE
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        message: 'There is an unknown error. Please get in touch with admin (11).',
        topic: 'Services'
      });
      break;
    case 12: // UNIMPLEMENTED
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Critical,
        message: 'UNIMPLEMENTED! (12)',
        topic: 'Services'
      });
      break;
    case 13: // INTERNAL
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Critical,
        message: 'There is an unknown error. Please get in touch with admin (13).',
        topic: 'Services'
      });
      break;
    case 14: // UNAVAILABLE
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Critical,
        message: 'The service is unavailable. Please wait a bit and try again (14).',
        topic: 'Services'
      });
      break;
    case 15: // DATA_LOSS
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Critical,
        message: 'Potential data loss. Please get in touch with admin (15).',
        topic: 'Services'
      });
      break;
    case 16: // UNAUTHENTICATED
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Info,
        message: 'Please login again (16).',
        topic: 'Services'
      });
      break;
    default:
      return '';
    }
  }

  private updateLoadingIndicator() {
    this.loadingSubject.next(this.concurrentRequests > 0);
  }

  async unary<T extends UnaryMethodDefinitionish>(
    methodDesc: T,
    _request: any,
    metadata: grpc.Metadata | undefined,
  ): Promise<any> {
    const token = await this._keycloakService.getToken();
    const options: GrpcCallOptions = {
      debug: false,
      metadata: new grpc.Metadata({ 'Authorization': `Bearer ${token}` }),
    };
    const request = { ..._request, ...methodDesc.requestType };
    const maybeCombinedMetadata = metadata && options.metadata
      ? new BrowserHeaders({ ...options?.metadata.headersMap, ...metadata?.headersMap })
      : metadata ?? options.metadata;
    this.concurrentRequests++;
    this.updateLoadingIndicator();

    return new Promise((resolve, reject) => {
      grpc.unary(methodDesc, {
        request,
        host: this.host,
        metadata: maybeCombinedMetadata ?? {},
        ...(options.transport !== undefined ? { transport: options.transport } : {}),
        debug: options.debug ?? false,
        onEnd:  (response) => {
          this.concurrentRequests--;
          this.updateLoadingIndicator();

          if (response.status === grpc.Code.OK) {
            resolve(response.message!.toObject());
          } else {
            this.handleError(response);
            reject('Error calling GRPC service');
          }
        },
      });
    });
  }

  invoke<T extends UnaryMethodDefinitionish>(
    methodDesc: T,
    _request: any,
    metadata: grpc.Metadata | undefined,
  ): Observable<any> {
    const result = this._keycloakService.getToken().then(token => {
      const options: GrpcCallOptions = {
        debug: false,
        metadata: new grpc.Metadata({ 'Authorization': `Bearer ${token}` }),
      };
      const upStreamCodes = options.upStreamRetryCodes ?? [];
      const DEFAULT_TIMEOUT_TIME: number = 3_000;
      const request = { ..._request, ...methodDesc.requestType };
      const transport = options.streamingTransport ?? options.transport;
      const maybeCombinedMetadata = metadata && options.metadata
        ? new BrowserHeaders({ ...options?.metadata.headersMap, ...metadata?.headersMap })
        : metadata ?? options.metadata;
      return new Observable((observer) => {
        this.concurrentRequests++;
        this.updateLoadingIndicator();
        const upStream = () => {
          const client = grpc.invoke(methodDesc, {
            host: this.host,
            request,
            ...(transport !== undefined ? { transport } : {}),
            metadata: maybeCombinedMetadata ?? {},
            debug: options.debug ?? false,
            onMessage: (next) => observer.next(next),
            onEnd: (code: grpc.Code, message: string, trailers: grpc.Metadata) => {
              if (code === 0) {
                observer.complete();
              } else if (upStreamCodes.includes(code)) {
                setTimeout(upStream, DEFAULT_TIMEOUT_TIME);
              } else {
                const err = new Error(message) as any;
                err.code = code;
                err.metadata = trailers;
                observer.error(err);
              }
            },
          });
          observer.add(() => {
            client.close();
          });
        };
        upStream();
      }).pipe(
        finalize(() => {
          this.concurrentRequests--;
          this.updateLoadingIndicator();
        }),
        share());
    });

    const result$: Observable<any> = from(result).pipe(
      switchMap((innerObservable: Observable<unknown>) => innerObservable)
    );

    return result$;
  }
}